<template>
  <div class="tenders-filters" :class="{ 'is-compact': isCompact }">
    <!-- Desktop / large tablet: full chip row -->
    <TendersFiltersChips v-if="!isCompact" class="tenders-filters__desktop" v-bind="chipProps" v-on="chipEvents" />

    <!-- Phones: the lists' phone filter bar (search and filters on one line, the filters in use on
         a line that scrolls sideways, every filter in its panel). -->
    <PhoneFilterBar v-if="isPhone" :active-count="activeFiltersCount" @clear-all="$emit('clear-all')">
      <template #search>
        <slot name="search" />
      </template>
      <template #filters>
        <TendersFiltersChips v-bind="chipProps" v-on="chipEvents" />
      </template>
    </PhoneFilterBar>

    <!-- Compact (iPad): one summary chip opens the stacked filters dialog -->
    <template v-else-if="isCompact">
      <FilterChip
        class="tenders-filters__compact-chip"
        is-text
        icon="sliders"
        :label="compactChipLabel"
        :active="hasActiveFilters || filtersDialogOpen"
        @toggle="filtersDialogOpen = true"
      />
      <BaseButton
        v-if="hasActiveFilters"
        class="tenders-filters__clearall"
        isSmall
        isTransparent
        isSquare
        hasIconLeft
        @click.native="$emit('clear-all')"
      >
        <BaseIcon name="x" />
        <span>{{ $t('filters.clear-all') }}</span>
      </BaseButton>
    </template>

    <BaseButton
      v-if="!isCompact && hasActiveFilters"
      class="tenders-filters__clearall"
      isSmall
      isTransparent
      isSquare
      hasIconLeft
      @click.native="$emit('clear-all')"
    >
      <BaseIcon name="x" />
      <span>{{ $t('filters.clear-all') }}</span>
    </BaseButton>

    <!-- Grows to push the maintenance banner to the trailing edge while space remains;
         when the banner wraps, it starts left-aligned on the next row. -->
    <div v-if="!isPhone" class="tenders-filters__push" aria-hidden="true"></div>

    <!-- Phones: one line, and a tap (there's no hover) shows which platforms. -->
    <v-popover
      v-if="affectedMaintenancePlatforms.length > 0"
      class="tenders-filters__maintenance"
      placement="bottom-end"
      :trigger="isPhone ? 'click' : 'hover'"
      popoverClass="basic-tooltip left-aligned"
      :delay="{ show: 300, hide: 150 }"
    >
      <BaseInfoBanner
        tone="warning"
        preset="soft"
        :title="
          $t(`page.tenders.views.filters.${isPhone ? 'platforms-maintenance-short' : 'platforms-maintenance'}`, {
            count: affectedMaintenancePlatforms.length,
          })
        "
      >
        <template #icon>
          <BaseIcon name="alert-triangle" />
        </template>
      </BaseInfoBanner>
      <template v-slot:popover>
        <p class="tenders-filters__maintenance-desc">
          {{ $t('page.tenders.views.filters.platforms-maintenance-details') }}
        </p>
        <div
          v-for="platform in affectedMaintenancePlatforms"
          :key="platform.id"
          class="tenders-filters__maintenance-row"
        >
          <span class="tenders-filters__maintenance-name">{{ platformName(platform) }}</span>
          <BaseTag tone="destructive" preset="soft">{{ $t('tags.maintenance') }}</BaseTag>
        </div>
      </template>
    </v-popover>

    <TendersFiltersDialog
      v-if="filtersDialogOpen"
      :search-filters="searchFilters"
      :platforms="userPlatforms"
      :users="users"
      :seao-active="seaoFiltersOn"
      :scoring-eligible="scoringEligible"
      :keywords-active="keywordsActive"
      :zones-active="zonesActive"
      :zones-badge="zonesBadge"
      :archived-active="archivedActive"
      :has-active-filters="hasActiveFilters"
      @close="filtersDialogOpen = false"
      @clear-all="$emit('clear-all')"
      @platform-change="$emit('platform-change', $event)"
      @seao-settings="onNestedSeaoSettings"
      @status-change="$emit('status-change', $event)"
      @closing-before-change="$emit('closing-before-change', $event)"
      @publication-change="onPublicationChange"
      @read-filter-change="$emit('read-filter-change', $event)"
      @follow-filter-change="$emit('follow-filter-change', $event)"
      @share-filter-change="$emit('share-filter-change', $event)"
      @assignees-filter-change="$emit('assignees-filter-change', $event)"
      @relevance-score-filter-change="$emit('relevance-score-filter-change', $event)"
      @open-keywords="onNestedOpenKeywords"
      @categories-change="onCategoriesChange"
      @zones-settings="onNestedZonesSettings"
      @archived-toggle="onArchivedToggle"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import BaseButton from '@/components/base/BaseButton';
import BaseIcon from '@/components/base/BaseIcon';
import BaseInfoBanner from '@/components/base/BaseInfoBanner';
import BaseTag from '@/components/base/BaseTag';
import FilterChip from '@/components/filters/FilterChip';
import PhoneFilterBar from '@/components/filters/PhoneFilterBar.vue';
import TendersFiltersChips from '@/components/page-tenders/views/TendersFiltersChips.vue';
import TendersFiltersDialog from '@/components/page-tenders/views/TendersFiltersDialog.vue';
import IsMobileMixin from '@/mixins/is-mobile-mixin';

import { datesArrayToObject } from '@/util/date-util';
import { getZonesCount } from '@/util/zones-util';
import { tenantHasTenderScoringAccess } from '@/util/module-access-util';
import { LOCALE } from '@/util/i18n/locale';
import { viewport } from '@/util/viewport';

/**
 * Tenders inbox filter bar — composed entirely from the shared filter chip kit (same chips as
 * Explore/Opportunities/Admin). Owns the page-level data the chips need (platform roster)
 * and re-emits per-dimension events to PageTenders. Keywords/Zones open external drawers/dialogs
 * and Archived is a plain toggle, so those three use bare FilterChips (no popover).
 */
export default {
  name: 'TendersFilters',
  mixins: [IsMobileMixin],
  components: {
    BaseButton,
    BaseIcon,
    BaseInfoBanner,
    BaseTag,
    FilterChip,
    PhoneFilterBar,
    TendersFiltersChips,
    TendersFiltersDialog,
  },
  props: {
    searchFilters: {
      type: Object,
      required: true,
    },
  },
  data() {
    return { filtersDialogOpen: false };
  },
  computed: {
    ...mapGetters('userPlatforms', ['userPlatforms']),
    ...mapGetters('tenant', ['users']),
    // iPad / mobile: one summary chip + stacked dialog. Desktop / large tablet: full chip row.
    isCompact() {
      return this.isMobile;
    },
    isPhone() {
      return viewport.size === 'phone';
    },
    chipProps() {
      return {
        searchFilters: this.searchFilters,
        platforms: this.userPlatforms,
        users: this.users,
        seaoActive: this.seaoFiltersOn,
        scoringEligible: this.scoringEligible,
        keywordsActive: this.keywordsActive,
        zonesActive: this.zonesActive,
        zonesBadge: this.zonesBadge,
        archivedActive: this.archivedActive,
      };
    },
    chipEvents() {
      const forward = (name) => (payload) => this.$emit(name, payload);
      return {
        'platform-change': forward('platform-change'),
        'seao-settings': forward('seao-settings'),
        'status-change': forward('status-change'),
        'closing-before-change': forward('closing-before-change'),
        'publication-change': this.onPublicationChange,
        'read-filter-change': forward('read-filter-change'),
        'follow-filter-change': forward('follow-filter-change'),
        'share-filter-change': forward('share-filter-change'),
        'assignees-filter-change': forward('assignees-filter-change'),
        'relevance-score-filter-change': forward('relevance-score-filter-change'),
        'open-keywords': forward('open-keywords'),
        'categories-change': this.onCategoriesChange,
        'zones-settings': forward('zones-settings'),
        'archived-toggle': this.onArchivedToggle,
        'clear-filter': forward('clear-filter'),
      };
    },
    scoringEligible() {
      const hasKeyword = (this.searchFilters.keywordIds || []).length > 0 || !!this.searchFilters.allKeywords;
      return tenantHasTenderScoringAccess() && hasKeyword;
    },
    seaoFiltersOn() {
      const regions = (this.searchFilters.administrativeRegions || {})['seao_v2'] || [];
      const seaoClass = (this.searchFilters.classifications || []).find((c) => c.scheme === 'SEAO');
      return regions.length > 0 || !!(seaoClass && seaoClass.codes && seaoClass.codes.length);
    },
    keywordsActive() {
      return (this.searchFilters.keywordIds || []).length > 0 || !!this.searchFilters.allKeywords;
    },
    zonesActive() {
      return this.zonesCount > 0;
    },
    zonesBadge() {
      return this.zonesCount ? `(${this.zonesCount})` : '';
    },
    archivedActive() {
      return this.searchFilters.archived === true;
    },
    zonesCount() {
      const z = this.searchFilters.zones;
      if (!z || typeof z !== 'object') return 0;
      try {
        return getZonesCount(z);
      } catch (e) {
        return 0;
      }
    },
    hasActiveFilters() {
      return this.activeFiltersCount > 0;
    },
    // Number of filter *dimensions* currently set (drives the compact chip label).
    activeFiltersCount() {
      const f = this.searchFilters || {};
      let count = 0;
      if ((f.platforms || []).length) count += 1;
      if ((f.statuses || []).length) count += 1;
      if (f.closingBefore) count += 1;
      if (f.publishedAfter || f.publishedBefore) count += 1;
      if ((f.readFilters || []).length) count += 1;
      if ((f.followFilters || []).length) count += 1;
      if ((f.shareFilters || []).length) count += 1;
      if ((f.assigneesFilters || []).length) count += 1;
      if ((f.relevanceScoreRange || []).length) count += 1;
      if ((f.procurementCategories || []).length) count += 1;
      if (this.keywordsActive) count += 1;
      if (this.zonesActive) count += 1;
      if (this.archivedActive) count += 1;
      return count;
    },
    compactChipLabel() {
      if (!this.activeFiltersCount) return this.$t('page.tenders.views.filters.compact-label');
      return this.$tc('page.tenders.views.filters.compact-active', this.activeFiltersCount, {
        count: this.activeFiltersCount,
      });
    },
    maintenancePlatforms() {
      return (this.userPlatforms || []).filter((p) => (p.tags || []).includes('maintenance'));
    },
    affectedMaintenancePlatforms() {
      const selected = this.searchFilters.platforms || [];
      if (!selected.length) return this.maintenancePlatforms;
      return this.maintenancePlatforms.filter((p) => selected.includes(p.id));
    },
  },
  watch: {
    // Leaving compact mode should dismiss the stacked dialog.
    isCompact(compact) {
      if (!compact) this.filtersDialogOpen = false;
    },
  },
  methods: {
    ...mapActions('userPlatforms', ['getUserPlatforms']),
    platformName(platform) {
      return this.$i18n.locale === LOCALE.FR_CA ? platform.nameFr : platform.nameEn;
    },
    onPublicationChange(dates) {
      this.$emit('publication-change', datesArrayToObject(dates) || { publishedAfter: null, publishedBefore: null });
    },
    onCategoriesChange(next) {
      const f = this.searchFilters || {};
      this.$emit('advanced-filters-change', {
        keywordIds: [...(f.keywordIds || [])],
        matchContexts: [...(f.matchContexts || [])],
        procurementCategories: next,
        zones: f.zones || {},
      });
    },
    onArchivedToggle() {
      this.$emit('archived-change', this.searchFilters.archived === true ? null : true);
    },
    // Nested drawers (keywords / zones / SEAO) should not stack on top of the compact filters dialog.
    onNestedOpenKeywords() {
      this.filtersDialogOpen = false;
      this.$emit('open-keywords');
    },
    onNestedZonesSettings() {
      this.filtersDialogOpen = false;
      this.$emit('zones-settings');
    },
    onNestedSeaoSettings() {
      this.filtersDialogOpen = false;
      this.$emit('seao-settings');
    },
    closePop() {
      this.filtersDialogOpen = false;
      const close = (vm) => {
        if (vm.$data && Object.prototype.hasOwnProperty.call(vm.$data, 'open')) vm.open = false;
        (vm.$children || []).forEach(close);
      };
      this.$children.forEach(close);
    },
  },
  mounted() {
    this.getUserPlatforms();
  },
};
</script>

<style lang="scss">
.tenders-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  // Phones: as wide as the screen, however long the line of filters in use (it scrolls instead).
  @include screen-below('tablet') {
    flex: 1 1 100%;
    min-width: 0;
  }

  &__clearall.base-button .base-button__element {
    color: $color-slate-500;
    font-weight: bold;
  }

  &__push {
    flex: 1 0 0;
    min-width: 0;
    height: 0;
    margin: 0;
    padding: 0;
    overflow: hidden;
    pointer-events: none;
  }

  &__maintenance {
    cursor: default;
  }

  &__maintenance-desc {
    margin: 0 0 8px;
    font-size: 12px;
    line-height: 1.4;
  }

  &__maintenance-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 4px 0;
    font-size: 12px;
  }

  &__maintenance-name {
    min-width: 0;
  }

  &.is-compact &__compact-chip .filter-chip__btn {
    min-height: 32px;
  }

  .phone-filter-bar {
    flex: 1 1 100%;
  }
}
</style>
